// web/src/copilot/PendingContextProvider.tsx
//
// Provides a one-shot pending-target slot consumed by the AskTapelens chat
// sheet. The sparkle button on a cluster/analysis card calls setPending() then
// dispatches a window event to open the chat; the sheet reads pending on open,
// registers it as a CopilotKit readable for the agent, and immediately
// clears() so it is not re-injected on the next user turn.
//
// Scope is hand-tuned: the provider lives BELOW <AuthGate> (so it is only
// active for authenticated routes) but at the route-element scope, mounted
// once in App.tsx around the dashboard subtree.
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { NoteTargetKind } from "../api/notes";

export type PendingContextValue = {
  kind: NoteTargetKind;
  id: string;
  title?: string;
};

type Ctx = {
  pending: PendingContextValue | null;
  setPending: (c: PendingContextValue | null) => void;
  clear: () => void;
};

const PendingContextCtx = createContext<Ctx | null>(null);

export function PendingContextProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingContextValue | null>(null);
  const value = useMemo<Ctx>(
    () => ({ pending, setPending, clear: () => setPending(null) }),
    [pending],
  );
  return (
    <PendingContextCtx.Provider value={value}>
      {children}
    </PendingContextCtx.Provider>
  );
}

export function usePendingContext(): Ctx {
  const v = useContext(PendingContextCtx);
  if (!v)
    throw new Error(
      "usePendingContext used outside PendingContextProvider",
    );
  return v;
}
